import some from 'lodash/some';
import { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';

import CustomerContext from 'components/customer/customer_context';
import { FILTERED_ITEMS, ITEM, UNREAD_DIVIDER } from './feed/feed_item_types';
import MarkConversationReadOnLeave from 'actions/conversation/mark_conversation_read_on_leave';
import UpdateAgentRead from 'actions/conversation/update_agent_read';
import { useExecuteAction } from 'components/hooks/connect_hooks';

/**
 * useManageAgentRead maintains the "New Activity" line in the feed based on where the agent has scrolled to and
 * what items they've seen, as well as updating the agentRead resource whenever the agent has scrolled to the
 * new items in the feed.
 */
export default function useManageAgentRead({ customerId, items, isLoadingAgentRead, lastReadTimestamp }) {
  const { latestConversationUpdatedAt, latestConversationId } = useContext(CustomerContext);
  const latestConvRef = useLatestConversationRef(latestConversationId, latestConversationUpdatedAt);

  // Place the New Activity line before this item
  const readUntilItemRef = useRef(null);
  // Latest item the agent has scrolled to
  const latestItemSeenRef = useRef(null);
  // Timestamp of the newest item the New Activity line has been positioned for
  const newestItemTimestampRef = useRef(null);
  // Whether the agent is currently scrolled to the bottom of the feed or not
  const isScrolledToBottomRef = useRef(false);

  // Used to initialize the New Activity line when customer is loaded
  const initialLastReadTimestamp = useInitialLastReadTimestamp(lastReadTimestamp);
  // The agent's read as it moves forward, so items already read before can't count as new.
  const readToRef = useRef(null);
  readToRef.current = getLaterTimestamp(lastReadTimestamp, initialLastReadTimestamp);
  // Whether we've initialized the New Activity line using the intiial last read timestamp yet or not
  const hasInitializedLastReadTimestampRef = useRef(false);

  const itemsWithNewActivityLine = useMemo(
    () => {
      const lastItem = getLastItem(items);

      // lastReadTimestamp may load after the feed - we want to initialize the New Activity line
      // position to that which our _initial_ lastReadTimestamp is when we first load the customer.
      if (!hasInitializedLastReadTimestampRef.current && (initialLastReadTimestamp || !isLoadingAgentRead)) {
        hasInitializedLastReadTimestampRef.current = true;
        newestItemTimestampRef.current = getSortTimestamp(lastItem);
        const index = getInitialNewActivityLineIndex(items, initialLastReadTimestamp);
        readUntilItemRef.current = index != null ? items[index] : null;
        return insertNewActivityLine(items, readUntilItemRef.current);
      }

      // We don't place the New Activity line until after we've initialized it with the intiial lastReadTimestamp
      if (!hasInitializedLastReadTimestampRef.current) {
        return items;
      }

      // `items` is rebuilt whenever anything about the customer's feed changes (typing, item updates, filters),
      // so only a newly arrived last item may move the line. Otherwise it stays where it is.
      if (isNewLastItem(lastItem, newestItemTimestampRef.current)) {
        newestItemTimestampRef.current = getSortTimestamp(lastItem);
        if (lastItem.initiatedByCurrentAgent) {
          readUntilItemRef.current = null;
        } else if (!isScrolledToBottomRef.current) {
          moveNewActivityLineDown(items, readUntilItemRef, latestItemSeenRef.current, readToRef.current);
        }
      }

      return insertNewActivityLine(items, readUntilItemRef.current);
    },
    // These dependencies are very critical - we only want to re-calculate the computed items when
    // items actually change or when our initialLastReadTimestamp becomes not-null.
    [initialLastReadTimestamp, items, isLoadingAgentRead]
  );

  // The visible range reported by the feed indexes into the items it renders, which include the New Activity line.
  const itemsRef = useRef(itemsWithNewActivityLine);
  useEffect(() => {
    itemsRef.current = itemsWithNewActivityLine;
  }, [itemsWithNewActivityLine]);

  useMarkReadOnLeave(customerId, itemsRef, latestItemSeenRef, readToRef, hasInitializedLastReadTimestampRef);

  useUpdateReadTo(
    customerId,
    latestItemSeenRef,
    hasInitializedLastReadTimestampRef,
    isScrolledToBottomRef,
    latestConvRef,
    itemsRef,
    readToRef
  );

  const onVisibleRangeChanged = useOnVisibleRangeChanged(
    latestConvRef,
    customerId,
    hasInitializedLastReadTimestampRef,
    isScrolledToBottomRef,
    itemsRef,
    latestItemSeenRef,
    readToRef
  );

  return [
    itemsWithNewActivityLine,
    onVisibleRangeChanged,
    {
      unreadLineIndex: getUnreadLineIndex(itemsWithNewActivityLine),
      latestItemSeenIndex: findItemIndexFromBack(itemsWithNewActivityLine, latestItemSeenRef.current),
    },
  ];
}

function useOnVisibleRangeChanged(
  latestConvRef,
  customerId,
  hasInitializedLastReadTimestampRef,
  isScrolledToBottomRef,
  itemsRef,
  latestItemSeenRef,
  readToRef
) {
  const [, rerender] = useState({});
  const executeAction = useExecuteAction();

  return useCallback(
    ({ start, end, isScrolledToBottom }) => {
      const items = itemsRef.current;
      const itemIndex = findItemIndexFromBack(items, latestItemSeenRef.current);
      if (end - 1 > itemIndex) {
        let minLastSeen = Math.max(start, itemIndex);
        let maxLastSeen = Math.min(end - 1, items.length - 1);
        for (let i = maxLastSeen; i >= minLastSeen; i--) {
          if (items[i] && items[i].type === ITEM) {
            latestItemSeenRef.current = items[i];
            rerender({});
            break;
          }
        }
      }

      if (isScrolledToBottom !== isScrolledToBottomRef.current) {
        isScrolledToBottomRef.current = isScrolledToBottom;
        rerender({});

        // If we're scrolled to the bottom, try to update the agent read to the conversation updated at time.
        // This is unfortunately necessary because the conversation updated at time may differ from the latest
        // item timestamp.. so if we don't update the agentRead to that time, the customer may appear unread
        // in the MessagesNotificationMenu and the Inbox.
        if (hasInitializedLastReadTimestampRef.current && isScrolledToBottom) {
          updateReadToConversation(executeAction, customerId, latestConvRef.current, items, readToRef.current);
        }
      }

      // We need to ensure that we've initialized the New Activity line with the initial lastReadTimestamp for the
      // customer, otherwise we may mark the latest item as read _before_ we've set the "New Activity" line for
      // the customer.. meaning we'll never see the "New Activity" line!
      if (hasInitializedLastReadTimestampRef.current) {
        updateReadToItem(executeAction, customerId, latestItemSeenRef.current, latestConvRef.current);
      }
    },
    [
      customerId,
      executeAction,
      hasInitializedLastReadTimestampRef,
      isScrolledToBottomRef,
      itemsRef,
      latestConvRef,
      latestItemSeenRef,
      readToRef,
    ]
  );
}

function updateReadToItem(executeAction, customerId, item, latestConversation) {
  // Items outside of a conversation, like tasks, have no read of their own, so they count toward the latest
  // conversation's read. Otherwise viewing them would never be recorded.
  const conversationId = item?.conversationId || latestConversation?.id;
  if (!item?.timestamp || !conversationId) {
    return;
  }

  executeAction(UpdateAgentRead, {
    customerId,
    agentReadAttrs: { readTo: item.timestamp },
    conversationId,
  });
}

function updateReadToConversation(executeAction, customerId, conversation, items, readTo) {
  // The conversation's updatedAt covers items a filter hides, which the agent hasn't seen.
  if (!conversation?.id || !conversation.updatedAt || hasHiddenUnreadItems(items, readTo)) {
    return;
  }

  executeAction(UpdateAgentRead, {
    customerId,
    agentReadAttrs: { readTo: conversation.updatedAt },
    conversationId: conversation.id,
  });
}

function getInitialNewActivityLineIndex(items, lastReadTimestamp) {
  const readThroughIndex = findLastIndexReadBy(items, lastReadTimestamp);
  return readThroughIndex === -1 ? null : getNewActivityLineIndex(items, readThroughIndex);
}

// The line goes before the first item from someone else after the ones read.
function getNewActivityLineIndex(items, readThroughIndex) {
  for (let i = readThroughIndex + 1; i < items.length; i++) {
    if (isItemFromOthers(items[i])) {
      return i;
    }
  }
  return null;
}

function findLastIndexReadBy(items, readTo) {
  if (!readTo) {
    return -1;
  }
  for (let i = items.length - 1; i >= 0; i--) {
    const timestamp = getSortTimestamp(items[i]);
    if (timestamp && new Date(timestamp) <= new Date(readTo)) {
      return i;
    }
  }
  return -1;
}

// Index of the last item the agent has read, either by scrolling to it on this visit or by an earlier read.
function getReadThroughIndex(items, latestItemSeen, readTo) {
  return Math.max(findItemIndexFromBack(items, latestItemSeen), findLastIndexReadBy(items, readTo));
}

// Items whose content hasn't loaded yet only have a sortTimestamp.
function getSortTimestamp(item) {
  return item ? item.timestamp || item.sortTimestamp : null;
}

function getLaterTimestamp(timestamp, otherTimestamp) {
  if (!timestamp || !otherTimestamp) {
    return timestamp || otherTimestamp;
  }
  return new Date(timestamp) >= new Date(otherTimestamp) ? timestamp : otherTimestamp;
}

// Returns the first non-null value for `lastReadTimestamp`, forever basically until we load a new customer.
function useInitialLastReadTimestamp(lastReadTimestamp) {
  const initialLastReadTimestampRef = useRef(lastReadTimestamp);
  if (lastReadTimestamp && !initialLastReadTimestampRef.current) {
    initialLastReadTimestampRef.current = lastReadTimestamp;
  }

  return initialLastReadTimestampRef.current;
}

function useUpdateReadTo(
  customerId,
  latestItemSeenRef,
  hasInitializedLastReadTimestampRef,
  isScrolledToBottomRef,
  latestConvRef,
  itemsRef,
  readToRef
) {
  const hasInitialized = hasInitializedLastReadTimestampRef.current;
  const hasLatestItemSeen = !!latestItemSeenRef.current;
  const isScrolledToBottom = isScrolledToBottomRef.current;
  const executeAction = useExecuteAction();

  // If the latest item changes, update the agentRead with its time if we're scrolled to bottom
  useEffect(() => {
    if (hasInitialized && hasLatestItemSeen) {
      updateReadToItem(executeAction, customerId, latestItemSeenRef.current, latestConvRef.current);
    }
  }, [
    customerId,
    executeAction,
    hasInitialized,
    hasLatestItemSeen,
    isScrolledToBottom,
    latestConvRef,
    latestItemSeenRef,
  ]);

  const conversation = latestConvRef.current ? latestConvRef.current : null;
  // If the conversation updated at changes, update the agentRead with its time if we're scrolled to bottom
  useEffect(() => {
    if (hasInitialized && isScrolledToBottom) {
      updateReadToConversation(executeAction, customerId, conversation, itemsRef.current, readToRef.current);
    }
  }, [customerId, executeAction, hasInitialized, conversation, isScrolledToBottom, itemsRef, readToRef]);
}

function useMarkReadOnLeave(customerId, itemsRef, latestItemSeenRef, readToRef, hasInitializedLastReadTimestampRef) {
  const executeAction = useExecuteAction();
  const refs = { itemsRef, latestItemSeenRef, readToRef, hasInitializedLastReadTimestampRef };

  // The refs are read when the feed unmounts on purpose: that's when we know what the agent saw.
  useEffect(() => () => markReadOnLeave(executeAction, customerId, refs), []); // eslint-disable-line react-hooks/exhaustive-deps
}

function markReadOnLeave(
  executeAction,
  customerId,
  { itemsRef, latestItemSeenRef, readToRef, hasInitializedLastReadTimestampRef }
) {
  if (!hasInitializedLastReadTimestampRef.current) {
    return;
  }
  executeAction(MarkConversationReadOnLeave, {
    customerId,
    hasUnseenItems: hasUnseenItems(itemsRef.current, latestItemSeenRef.current, readToRef.current),
  });
}

function hasUnseenItems(items, latestItemSeen, readTo) {
  const readThroughIndex = getReadThroughIndex(items, latestItemSeen, readTo);
  return items.some((item, i) => i > readThroughIndex && isItemFromOthers(item)) || hasHiddenUnreadItems(items, readTo);
}

// Items from others that a filter hides can't be seen until it's cleared.
function hasHiddenUnreadItems(items, readTo) {
  return items.some(
    item =>
      item.type === FILTERED_ITEMS &&
      !!item.newestFromOthersTimestamp &&
      (!readTo || new Date(item.newestFromOthersTimestamp) > new Date(readTo))
  );
}

// Only items from someone else count as new activity.
export function isItemFromOthers(item) {
  return !!item && item.type === ITEM && !item.initiatedByCurrentAgent;
}

function useLatestConversationRef(latestConversationId, latestConversationUpdatedAt) {
  const latestConvRef = useRef({ id: latestConversationId, updatedAt: latestConversationUpdatedAt });
  useEffect(() => {
    latestConvRef.current = {
      id: latestConversationId,
      updatedAt: latestConversationUpdatedAt,
    };
  }, [latestConversationId, latestConversationUpdatedAt]);
  return latestConvRef;
}

function getLastItem(items) {
  if (!items.length) {
    return null;
  }

  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i].type === ITEM) {
      return items[i];
    }
  }

  return null;
}

// An item newer than any the line was positioned for, as opposed to an older one becoming last because the newest
// was removed or filtered out, or coming back when that filter is cleared.
function isNewLastItem(lastItem, newestItemTimestamp) {
  const timestamp = getSortTimestamp(lastItem);
  if (!timestamp) {
    return false;
  }
  return !newestItemTimestamp || new Date(timestamp) > new Date(newestItemTimestamp);
}

// Moves the line after everything the agent has read, but never back above where it already is.
function moveNewActivityLineDown(items, readUntilItemRef, latestItemSeen, readTo) {
  const readThroughIndex = Math.max(
    getReadThroughIndex(items, latestItemSeen, readTo),
    findItemIndexFromBack(items, readUntilItemRef.current) - 1
  );
  if (readThroughIndex === -1) {
    return;
  }
  const index = getNewActivityLineIndex(items, readThroughIndex);
  readUntilItemRef.current = index != null ? items[index] : null;
}

function insertNewActivityLine(items, item) {
  if (item == null) {
    return items;
  }

  const unreadItem = {
    type: UNREAD_DIVIDER,
    id: 'unread-divider',
  };

  let index = findItemIndexFromBack(items, item);
  if (index < 0) {
    return items;
  }

  // HACK: Don't try to place the New Activity line after all the actual conversation items. It is definitely a bug
  // that we tried to place it after to start, so this is a guard if that situation arises.
  const after = items.slice(index);
  if (!after.length || !some(after, afterItem => afterItem.type === ITEM)) {
    return items;
  }

  // Splicing in the unread items line must ensure the item that comes afterwards is not stacked.
  if (after[0].type === ITEM) {
    after[0] = {
      ...after[0],
      isStacked: false,
    };
  }

  return [...items.slice(0, index), unreadItem, ...after];
}

// Index of the first item after the New Activity line, or -1 when the line isn't in the feed.
function getUnreadLineIndex(items) {
  const dividerIndex = items.findIndex(item => item.type === UNREAD_DIVIDER);
  return dividerIndex === -1 ? -1 : dividerIndex + 1;
}

function findItemIndexFromBack(items, item) {
  let index = -1;
  if (item && items.length) {
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i].id === item.id) {
        index = i;
        break;
      }
    }
  }
  return index;
}
